import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Bar, BarChart, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useChartTheme } from 'src/components/livy/LivyChart';
import { BreakdownChart, ChartCard } from './UsageCharts';
// ponytail: by name, as no company record marks a test company (TestMode is unset on all of them).
// Every match is listed under the charts so a wrong one is visible.
export const isLivableTestCompany = name => /\b(livable|test|sandbox|demo)\b/i.test(name);
/** Per UTC day, zero-filled, with customer turns from Livable's own test companies split out. */
export function audienceSeries(stats, isTest) {
  const days = [];
  const customerTurns = {};
  const seen = new Set();
  const end = stats.to.slice(0, 10);
  for (let d = new Date("".concat(stats.from.slice(0, 10), "T00:00:00Z"));; d.setUTCDate(d.getUTCDate() + 1)) {
    var _stats$dailyAudience, _asked$staff, _asked$public, _asked$unknown;
    const day = d.toISOString().slice(0, 10);
    const asked = (_stats$dailyAudience = stats.dailyAudience) === null || _stats$dailyAudience === void 0 ? void 0 : _stats$dailyAudience[day];
    const point = {
      day,
      customer: 0,
      test: 0,
      staff: (_asked$staff = asked === null || asked === void 0 ? void 0 : asked.staff) !== null && _asked$staff !== void 0 ? _asked$staff : 0,
      public: (_asked$public = asked === null || asked === void 0 ? void 0 : asked.public) !== null && _asked$public !== void 0 ? _asked$public : 0,
      unknown: (_asked$unknown = asked === null || asked === void 0 ? void 0 : asked.unknown) !== null && _asked$unknown !== void 0 ? _asked$unknown : 0,
      companies: 0,
      companiesToDate: 0
    };
    for (const _ref of Object.entries((_stats$customerDaily$ = (_stats$customerDaily = stats.customerDaily) === null || _stats$customerDaily === void 0 ? void 0 : _stats$customerDaily[day]) !== null && _stats$customerDaily$ !== void 0 ? _stats$customerDaily$ : {})) {
      var _stats$customerDaily$, _stats$customerDaily;
      var _ref2 = _slicedToArray(_ref, 2);
      const company = _ref2[0];
      const turns = _ref2[1];
      if (isTest(company)) {
        point.test += turns;
      } else {
        var _customerTurns$compan;
        point.customer += turns;
        point.companies += 1;
        seen.add(company);
        customerTurns[company] = ((_customerTurns$compan = customerTurns[company]) !== null && _customerTurns$compan !== void 0 ? _customerTurns$compan : 0) + turns;
      }
    }
    point.companiesToDate = seen.size;
    days.push(point);
    if (day >= end || days.length > 400) break;
  }
  return {
    days,
    customerTurns
  };
}
const BARS = [['customer', 'Customers'], ['test', 'Livable test companies'], ['staff', 'Livable staff'], ['public', 'Public site'], ['unknown', 'Unknown (before Sep 11)']];
const shortDay = day => day.slice(5);
export function AudienceCharts(_ref3) {
  var _stats$customerDaily2;
  let stats = _ref3.stats,
    companyName = _ref3.companyName;
  const _useChartTheme = useChartTheme(),
    series = _useChartTheme.series,
    axis = _useChartTheme.axis,
    grid = _useChartTheme.grid,
    tooltipProps = _useChartTheme.tooltipProps;
  const axisProps = {
    tick: {
      fill: axis,
      fontSize: 11
    },
    tickLine: false,
    axisLine: {
      stroke: grid
    }
  };
  const legend = {
    wrapperStyle: {
      fontSize: 11,
      color: axis
    }
  };
  if (!stats.audience) return null;
  const isTest = id => isLivableTestCompany(companyName(id));
  const _audienceSeries = audienceSeries(stats, isTest),
    days = _audienceSeries.days,
    customerTurns = _audienceSeries.customerTurns;
  const total = Object.values(stats.audience).reduce((a, b) => a + b, 0);
  const real = Object.values(customerTurns).reduce((a, b) => a + b, 0);
  const companies = Object.keys(customerTurns).length;
  const excluded = [...new Set(Object.values((_stats$customerDaily2 = stats.customerDaily) !== null && _stats$customerDaily2 !== void 0 ? _stats$customerDaily2 : {}).flatMap(Object.keys).filter(isTest).map(companyName))];
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      mb: '20px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: '10px'
    }
  }, /*#__PURE__*/React.createElement("strong", null, companies), " customer ", companies === 1 ? 'company' : 'companies', " outside Livable,", ' ', /*#__PURE__*/React.createElement("strong", null, real), " of ", total, " turns (", total ? (real / total * 100).toFixed(0) : 0, "%).", excluded.length > 0 && " Counted as Livable test companies: ".concat(excluded.join(', '), ".")), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'grid',
      gap: '14px',
      gridTemplateColumns: {
        xs: '1fr',
        lg: '2fr 2fr 1fr'
      }
    }
  }, /*#__PURE__*/React.createElement(ChartCard, {
    title: "Turns by who asked (UTC)"
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: 220
  }, /*#__PURE__*/React.createElement(BarChart, {
    data: days,
    margin: {
      top: 4,
      right: 4,
      bottom: 0,
      left: 0
    }
  }, /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "day",
    tickFormatter: shortDay,
    minTickGap: 16
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, _extends({
    allowDecimals: false,
    width: 32
  }, axisProps)), /*#__PURE__*/React.createElement(Tooltip, tooltipProps), /*#__PURE__*/React.createElement(Legend, legend), BARS.map((_ref4, i) => {
    let _ref5 = _slicedToArray(_ref4, 2),
      key = _ref5[0],
      name = _ref5[1];
    return /*#__PURE__*/React.createElement(Bar, {
      key: key,
      dataKey: key,
      name: name,
      stackId: "a",
      fill: series[i]
    });
  })))), /*#__PURE__*/React.createElement(ChartCard, {
    title: "Customer companies using Livy (UTC)"
  }, /*#__PURE__*/React.createElement(ResponsiveContainer, {
    width: "100%",
    height: 220
  }, /*#__PURE__*/React.createElement(ComposedChart, {
    data: days,
    margin: {
      top: 4,
      right: 4,
      bottom: 0,
      left: 0
    }
  }, /*#__PURE__*/React.createElement(XAxis, _extends({
    dataKey: "day",
    tickFormatter: shortDay,
    minTickGap: 16
  }, axisProps)), /*#__PURE__*/React.createElement(YAxis, _extends({
    allowDecimals: false,
    width: 32
  }, axisProps)), /*#__PURE__*/React.createElement(Tooltip, tooltipProps), /*#__PURE__*/React.createElement(Legend, legend), /*#__PURE__*/React.createElement(Bar, {
    dataKey: "companies",
    name: "That day",
    fill: series[0],
    radius: [3, 3, 0, 0]
  }), /*#__PURE__*/React.createElement(Line, {
    dataKey: "companiesToDate",
    name: "So far in window",
    stroke: series[1],
    dot: false,
    strokeWidth: 2
  })))), /*#__PURE__*/React.createElement(BreakdownChart, {
    title: "Customer turns by company",
    data: Object.entries(customerTurns).sort((a, b) => b[1] - a[1]).map(_ref6 => {
      let _ref7 = _slicedToArray(_ref6, 2),
        id = _ref7[0],
        n = _ref7[1];
      return [companyName(id), n];
    }),
    format: n => String(n)
  })));
}